import { useCallback, useMemo } from 'react';
import { useLocation } from '@packages/router-dom';
import { ErrorStatuses, RecentDetailPageDataDefaultOptions, STORAGE_LIMIT } from '../const';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import {
	DetailPageParams,
	GetRecentDetailPagesDropdownItemsFn,
	GetRecentDetailPagesFn,
	RecentDetailPageItem,
	RecentDetailPagesStorageAPI,
	RemoveRecentDetailPageDataFn,
	SetRecentDetailPageDataFn,
	TryRemoveRecentDetailPageDataFn,
	UseRecentDetailPagesStorageParams,
} from '../types';
import { useUserPageContextState } from '../../../modules/user';
import { cleanUpUrl, formatDetailPageParams } from '../utils';
import { LocalStorageKeys, storageManager } from '../../../utils/storageManager';
import { PerUserLocalStorageKey } from '../../../utils/storageManager/const';
import { isNull, isNullOrUndefined } from '../../../predicates/type';
import { useAESCipherAlgorithm } from '../../../hooks/useAESCipherAlgorithm';
import { useCallbackRef } from '../../../hooks/useCallbackRef';
import { IS_DEVELOPMENT_ANY } from '../../../const/env';

export const useRecentlyViewedStorageAPI = (params?: UseRecentDetailPagesStorageParams) => {
	const listPageTypeId = params && params.listPageTypeId;

	const { id: userId } = useUserPageContextState();
	const { encrypt, decrypt } = useAESCipherAlgorithm<RecentDetailPageItem[]>(String(userId));

	const primaryStorageKey = `${PerUserLocalStorageKey}.${LocalStorageKeys.DetailPageRecentItems}.${userId}`;
	const { pathname, search } = useLocation();

	const getStoredItemsByListPageTypeId = () => {
		if (IS_DEVELOPMENT_ANY && isNullOrUndefined(listPageTypeId)) {
			throw new Error('Please provide listPageTypeId for this action');
		}

		const items = storageManager.getSubItem<string>(primaryStorageKey, String(listPageTypeId));

		if (isNull(items)) return [];

		return decrypt(items);
	};

	const getAllStoredRecentDetailPages = useCallback<() => null | Record<number, RecentDetailPageItem[]>>(() => {
		const items = storageManager.getItem<Record<number, string> | null>(primaryStorageKey);

		if (isNull(items)) return null;

		const decrypted: Record<number, RecentDetailPageItem[]> = {};

		for (const key in items) {
			decrypted[key] = decrypt(items[key]);
		}

		return decrypted;
	}, [decrypt, primaryStorageKey]);

	const setStoredRecentDetailPagesByListPageTypeId = useCallback(
		(listPageTypeId: number | string, newItems: RecentDetailPageItem[]) => {
			storageManager.setSubItem(primaryStorageKey, String(listPageTypeId), encrypt(newItems));
		},
		[encrypt, primaryStorageKey],
	);

	const storeItems = useCallback(
		(newItems: RecentDetailPageItem[]) => {
			if (IS_DEVELOPMENT_ANY && isNullOrUndefined(listPageTypeId)) {
				throw new Error('Please provide listPageTypeId for this action');
			}

			setStoredRecentDetailPagesByListPageTypeId(listPageTypeId, newItems);
		},
		[listPageTypeId, setStoredRecentDetailPagesByListPageTypeId],
	);

	const isAlreadyStored = (detailPageParams: DetailPageParams) => {
		const storedItems = getStoredItemsByListPageTypeId();

		return storedItems.some((item) => isEqual(item.detailPageParams, detailPageParams));
	};

	const addNewItem = (newItem: RecentDetailPageItem) => {
		const storedItems = getStoredItemsByListPageTypeId();

		const newItems = [newItem, ...storedItems];

		if (newItems.length > STORAGE_LIMIT) {
			newItems.length = STORAGE_LIMIT;
		}

		storeItems(newItems);
	};

	const refreshItem = ({ detailPageParams, detailPageData }: Partial<RecentDetailPageItem>) => {
		const storedItems = getStoredItemsByListPageTypeId();

		const targetItem = storedItems.find((item) => isEqual(item.detailPageParams, detailPageParams))!;
		const restItems = storedItems.filter((i) => !isEqual(i.detailPageParams, detailPageParams));
		const isDataEqual = isEqual(targetItem.detailPageData, detailPageData);

		let newItem: Partial<RecentDetailPageItem>;
		if (isDataEqual) {
			newItem = {
				...targetItem,
				visitDate: Date.now(),
			}; // move the item to the top
		} else {
			// update the item with actual data and move to the top
			newItem = {
				...targetItem,
				detailPageData,
				visitDate: Date.now(),
			};
		}

		const newItems: RecentDetailPageItem[] = [newItem as RecentDetailPageItem, ...restItems];
		storeItems(newItems);
	};

	const removeRecentDetailPageData = useCallbackRef<RemoveRecentDetailPageDataFn>(({ detailPageParams }) => {
		if (IS_DEVELOPMENT_ANY && isNullOrUndefined(listPageTypeId)) {
			throw new Error('Please provide listPageTypeId for this action');
		}

		const formattedDetailParams = formatDetailPageParams(detailPageParams);
		const storedItems = getStoredItemsByListPageTypeId();

		if (isAlreadyStored(formattedDetailParams)) {
			const newItems = storedItems.filter((item) => !isEqual(item.detailPageParams, formattedDetailParams));
			storeItems(newItems);
		}
	});

	const setRecentDetailPageData = useCallbackRef<SetRecentDetailPageDataFn>(
		({ detailPageParams, value, meta, options = RecentDetailPageDataDefaultOptions }) => {
			if (IS_DEVELOPMENT_ANY && isNullOrUndefined(listPageTypeId)) {
				throw new Error('Please provide listPageTypeId for this action');
			}

			if (isEmpty(detailPageParams) || !value) {
				return;
			}

			const formattedDetailParams = formatDetailPageParams(detailPageParams);

			if (isAlreadyStored(formattedDetailParams)) {
				return refreshItem({
					detailPageParams: formattedDetailParams,
					detailPageData: { meta, value },
				});
			}

			addNewItem({
				detailPageUrl: options.detailPageUrl ? options.detailPageUrl : cleanUpUrl(`${pathname}${search}`),
				detailPageParams: formattedDetailParams,
				detailPageData: { value, meta },
				visitDate: Date.now(),
				listPageTypeId,
			});
		},
	);

	const getRecentDetailPagesDropdownItems = useCallbackRef<GetRecentDetailPagesDropdownItemsFn>(() => {
		if (IS_DEVELOPMENT_ANY && isNullOrUndefined(listPageTypeId)) {
			throw new Error('Please provide listPageTypeId for this action');
		}

		const storedItems = getStoredItemsByListPageTypeId();

		return storedItems.map((item) => {
			return {
				id: item.detailPageParams.id,
				...item.detailPageData,
			};
		});
	});

	const getAllRecentDetailPages = () => {
		const allStoredItems = getAllStoredRecentDetailPages();

		if (isNull(allStoredItems)) return [];

		let allStoredItemsArray: RecentDetailPageItem[] = [];

		for (const key in allStoredItems) {
			if (allStoredItems.hasOwnProperty(key)) {
				allStoredItemsArray = allStoredItemsArray.concat(allStoredItems[key]);
			}
		}

		return allStoredItemsArray;
	};

	const getRecentDetailPages = useCallbackRef<GetRecentDetailPagesFn>(() => {
		if (!isNullOrUndefined(listPageTypeId)) {
			return getStoredItemsByListPageTypeId();
		}

		return getAllRecentDetailPages();
	});

	const tryRemoveRecentDetailPageData = useCallbackRef<TryRemoveRecentDetailPageDataFn>((error, detailPageParams) => {
		if (error.response && ErrorStatuses.includes(error.response.status)) {
			removeRecentDetailPageData({ detailPageParams });
		}
	});

	return useMemo<RecentDetailPagesStorageAPI>(
		() => ({
			setRecentDetailPageItems: storeItems,
			setRecentDetailPageData,
			getRecentDetailPages,
			removeRecentDetailPageData,
			tryRemoveRecentDetailPageData,
			getRecentDetailPagesDropdownItems,
			getAllStoredRecentDetailPages,
			setStoredRecentDetailPagesByListPageTypeId,
		}),
		[
			storeItems,
			setRecentDetailPageData,
			getRecentDetailPages,
			removeRecentDetailPageData,
			tryRemoveRecentDetailPageData,
			getRecentDetailPagesDropdownItems,
			getAllStoredRecentDetailPages,
			setStoredRecentDetailPagesByListPageTypeId,
		],
	);
};
